import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, Input, Textarea, Button, Switch } from '@crisellevaldez/speculo-ui';
import { useQueryClient } from '@tanstack/react-query';
import { Tenant, updateTenantSchema, UpdateTenantData } from '../../../../schemas/tenant.schema';
import { tenantManagementApi } from '../../../../api/tenantManagement';
import { useUpdateCitySubscription } from '../../../../hooks/cities/useUpdateCitySubscription';
import { useDnsStatus } from '../../../../hooks/client/useDnsStatus';

import {
  StickyNote,
  Palette,
  BarChart,
  MapPin,
  Globe,
  ExternalLink,
  Edit2,
  RotateCw,
  CheckCircle,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { IconPill } from '../../../common/IconPill';
import { CitySelector } from './CitySelector';
import { ChangeDomainModal } from './ChangeDomainModal';
import { DnsRecordsDisplay } from '../../onboarding/components/DnsRecordsDisplay';

interface UpdateWebsiteFormProps {
  tenant: Tenant;
  organizationId: string;
  onSuccess: () => void;
  onError: (error: Error) => void;
  onSubmitting: (isSubmitting: boolean) => void;
  onCitiesLoading: (loading: boolean) => void;
  onBrandingClick?: () => void;
  onMLSClick?: () => void;
  isSuperAdmin?: boolean;
  /** Read-only for non-admins: disables the editable inputs (name, skip
   *  rentals) while keeping Branding/MLS view launchers clickable. */
  locked?: boolean;
}

export const UpdateWebsiteForm: React.FC<UpdateWebsiteFormProps> = ({
  tenant,
  organizationId,
  onSuccess,
  onError,
  onSubmitting,
  onCitiesLoading,
  onBrandingClick,
  onMLSClick,
  isSuperAdmin = false,
  locked = false,
}) => {
  const [selectedCities, setSelectedCities] = useState<string[]>(tenant.cities || []);
  const [showChangeDomainModal, setShowChangeDomainModal] = useState(false);

  // Auto-check DNS status on component mount
  const {
    dnsStatus,
    refetchDns,
    error: dnsError,
  } = useDnsStatus({
    organizationId,
    domain: tenant.domain?.primary,
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<UpdateTenantData>({
    resolver: zodResolver(updateTenantSchema),
    defaultValues: {
      name: tenant.name,
      analytics: {
        googleTag: tenant.analytics?.googleTag || '',
        gtmHead: tenant.analytics?.gtmHead || '',
        gtmBody: tenant.analytics?.gtmBody || '',
        gaHead: tenant.analytics?.gaHead || '',
      },
      skipRentals: tenant.skipRentals || false,
    },
    mode: 'onSubmit',
  });

  const queryClient = useQueryClient();
  const { mutateAsync: updateCitySubscription } = useUpdateCitySubscription();

  const onSubmit = async (data: UpdateTenantData) => {
    onSubmitting(true);
    try {
      const updatedData: UpdateTenantData = {
        name: data.name,
        analytics: {
          googleTag: data.analytics.googleTag || '',
          gtmHead: data.analytics.gtmHead || '',
          gtmBody: data.analytics.gtmBody || '',
          gaHead: data.analytics.gaHead || '',
        },
        // Preserve existing branding when saving website settings.
        // The tenant update endpoint can behave like a full document update,
        // so omitting branding here drops nested values like propertyDetails.
        branding: tenant.branding,
        skipRentals: data.skipRentals,
      };

      // Update tenant
      await tenantManagementApi.updateTenant(organizationId, updatedData);

      // Update cities using the custom hook that handles deletion
      const currentCities = tenant.cities || [];
      await updateCitySubscription({
        organizationId,
        selectedCities,
        currentCities,
      });

      // Call analytics setup whenever any analytics field changed, including clears
      const analyticsChanged =
        data.analytics.googleTag !== tenant.analytics?.googleTag ||
        data.analytics.gtmHead !== tenant.analytics?.gtmHead ||
        data.analytics.gtmBody !== tenant.analytics?.gtmBody ||
        data.analytics.gaHead !== tenant.analytics?.gaHead;

      if (analyticsChanged) {
        await tenantManagementApi.analyticsSetup({
          organization_id: organizationId,
          googleTag: data.analytics.googleTag || '',
          gtmHead: data.analytics.gtmHead || '',
          gtmBody: data.analytics.gtmBody || '',
          gaHead: data.analytics.gaHead || '',
        });
      }

      await queryClient.invalidateQueries({ queryKey: ['tenant', organizationId] });
      await queryClient.invalidateQueries({ queryKey: ['organization', organizationId] });
      await queryClient.invalidateQueries({ queryKey: ['Organizations'] });
      onSuccess();
    } catch (error) {
      onError(error instanceof Error ? error : new Error('An error occurred'));
    } finally {
      onSubmitting(false);
    }
  };

  const handleDomainChangeSuccess = async () => {
    // Invalidate queries to refresh tenant data
    await queryClient.invalidateQueries({ queryKey: ['tenant', organizationId] });
    onSuccess();
  };

  return (
    <>
      <form id="update-website-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="bg-gray-50 dark:bg-white/5 p-6 rounded-lg border border-gray-300 dark:border-white/15">
          <div className="flex items-center gap-4 mb-3">
            <IconPill icon={StickyNote} size="lg" />
            <h4 className="text-sm font-bold text-foreground">IDX Site Setup</h4>
          </div>

          <hr className="dark:border-white/10" />
          <div className="space-y-6 mt-5">
            <div className="grid grid-cols-2 gap-6">
              <Form.Control>
                <Form.Label>Name</Form.Label>
                <Input
                  {...register('name')}
                  error={errors.name?.message}
                  placeholder="Enter website name"
                  disabled={locked}
                />
              </Form.Control>
              <div>
                <Form.Label>Primary Domain</Form.Label>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex-1 px-3 py-2 bg-gray-100 dark:bg-white/10 border border-gray-200 dark:border-white/10 rounded-md text-sm text-gray-700 dark:text-gray-300">
                    {tenant.domain?.primary || 'No domain configured'}
                  </div>
                  {tenant.domain?.primary && (
                    <>
                      <a
                        href={`https://${tenant.domain.primary}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:text-primary/80 transition-colors flex-shrink-0 p-2"
                        title="Open domain"
                      >
                        <Globe className="w-5 h-5" />
                      </a>
                      {isSuperAdmin && (
                        <Button
                          variant="outline"
                          type="button"
                          size="sm"
                          onClick={() => setShowChangeDomainModal(true)}
                          className="flex items-center gap-1"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      )}
                    </>
                  )}
                </div>
                {/* DNS Status and Actions */}
                {tenant.domain?.primary && (
                  <div className="mt-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button
                        variant={dnsStatus === 'connected' ? 'outline' : 'primary'}
                        type="button"
                        size="sm"
                        onClick={() => refetchDns()}
                        disabled={dnsStatus === 'loading'}
                        className={`flex items-center gap-1 ${
                          dnsStatus === 'connected'
                            ? '!bg-green-600 !border-green-600 !text-white hover:!bg-green-500'
                            : dnsStatus === 'need_setup'
                              ? 'bg-primary text-black'
                              : ''
                        }`}
                      >
                        {dnsStatus === 'loading' ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : dnsStatus === 'connected' ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : dnsStatus === 'need_setup' || dnsStatus === 'error' ? (
                          <AlertCircle className="w-4 h-4 text-black" />
                        ) : (
                          <RotateCw className="w-4 h-4" />
                        )}
                        {dnsStatus === 'loading'
                          ? 'Checking...'
                          : dnsStatus === 'connected'
                            ? 'DNS Connected'
                            : dnsStatus === 'need_setup'
                              ? 'DNS Not Configured'
                              : dnsStatus === 'error'
                                ? 'Check Failed'
                                : 'Check DNS'}
                      </Button>
                      <DnsRecordsDisplay
                        idxSubdomain={tenant.domain.primary.split('.')[0]}
                        buttonLabel="View DNS Records"
                      />
                    </div>
                    {dnsStatus === 'need_setup' && (
                      <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-2">
                        DNS not configured. If you already added the DNS records, it may take up to
                        24 hours for propagation.
                      </p>
                    )}
                    {dnsStatus === 'error' && dnsError && (
                      <p className="text-sm text-red-600 mt-2">{dnsError}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-6 border border-gray-300 dark:border-white/15">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <IconPill icon={Palette} size="lg" />
              <h4 className="text-sm font-bold text-foreground">Branding & Styles</h4>
            </div>
            <Button
              variant="outline"
              type="button"
              onClick={(e) => {
                e.preventDefault();
                onBrandingClick?.();
              }}
            >
              {locked ? 'View Branding' : 'Update Branding'}
            </Button>
          </div>
        </div>

        {isSuperAdmin && (
          <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-6 border border-gray-300 dark:border-white/15">
            <div className="flex items-center gap-4 mb-3">
              <IconPill icon={BarChart} size="lg" />
              <h4 className="text-sm font-bold text-foreground">Analytics Configuration</h4>
            </div>

            <hr className="dark:border-white/10" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
              <Form.Control>
                <Form.Label>Google Tag</Form.Label>
                <Textarea
                  {...register('analytics.googleTag')}
                  error={errors.analytics?.googleTag?.message}
                  placeholder="Enter Google Tag script"
                  rows={3}
                />
              </Form.Control>
              <Form.Control>
                <Form.Label>GTM Head</Form.Label>
                <Textarea
                  {...register('analytics.gtmHead')}
                  error={errors.analytics?.gtmHead?.message}
                  placeholder="Enter GTM Head script"
                  rows={3}
                />
              </Form.Control>
              <Form.Control>
                <Form.Label>GTM Body</Form.Label>
                <Textarea
                  {...register('analytics.gtmBody')}
                  error={errors.analytics?.gtmBody?.message}
                  placeholder="Enter GTM Body script"
                  rows={3}
                />
              </Form.Control>
              <Form.Control>
                <Form.Label>GA Head</Form.Label>
                <Textarea
                  {...register('analytics.gaHead')}
                  error={errors.analytics?.gaHead?.message}
                  placeholder="Enter GA Head script"
                  rows={3}
                />
              </Form.Control>
            </div>
          </div>
        )}

        <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-6 border border-gray-300 dark:border-white/15">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-4">
              <IconPill icon={MapPin} size="lg" />
              <h4 className="text-sm font-bold text-foreground">MLS Management</h4>
            </div>
            <Button
              variant="outline"
              type="button"
              onClick={(e) => {
                e.preventDefault();
                onMLSClick?.();
              }}
            >
              {isSuperAdmin ? 'Manage/Subscribe MLS' : 'View MLS'}
            </Button>
          </div>

          <hr className="dark:border-white/10" />
          <div className="mt-5">
            <div className="flex items-center gap-3 mb-1">
              <Form.Label className="mb-1">Skip Rental Listings</Form.Label>
              <Controller
                name="skipRentals"
                control={control}
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onChange={field.onChange}
                    aria-label="Skip rental listings"
                    size="sm"
                    disabled={locked}
                  />
                )}
              />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              When enabled, rental properties will not be displayed on the website.
            </p>
          </div>
        </div>

        {isSuperAdmin && (
          <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-6 border border-gray-300 dark:border-white/15">
            <div className="flex items-center gap-4 mb-3">
              <IconPill icon={MapPin} size="lg" />
              <h4 className="text-sm font-bold text-foreground">City Management</h4>
            </div>

            <hr className="dark:border-white/10" />
            <div className="mt-5 space-y-6">
              <div>
                <CitySelector
                  organizationId={organizationId}
                  selectedCities={selectedCities}
                  onChange={setSelectedCities}
                  onLoadingChange={onCitiesLoading}
                />
              </div>

              {/* Display subscribed cities as clickable links */}
              {tenant.cities && tenant.cities.length > 0 && tenant.domain?.primary && (
                <div className="mt-4">
                  <div className="bg-white dark:bg-white/5 p-3 border border-gray-200 dark:border-white/10 rounded-lg max-h-60 overflow-y-auto">
                    <div className="space-y-2">
                      {tenant.cities.map((cityId) => {
                        const url = `https://${tenant.domain.primary}/${cityId}/homes-for-sale`;
                        const displayName = cityId
                          .split('-')
                          .map((word) =>
                            word.length <= 2
                              ? word.toUpperCase()
                              : word.charAt(0).toUpperCase() + word.slice(1)
                          )
                          .join(' ');

                        return (
                          <div key={cityId} className="flex items-center justify-between">
                            <span className="text-sm text-gray-800 dark:text-gray-200">{displayName}</span>
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primaryfortext underline hover:text-primary/80 flex items-center gap-1 text-sm"
                            >
                              <span>{cityId}/homes-for-sale</span>
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </form>

      {/* Change Domain Modal */}
      {tenant.domain?.primary && (
        <ChangeDomainModal
          isOpen={showChangeDomainModal}
          onClose={() => setShowChangeDomainModal(false)}
          onSuccess={handleDomainChangeSuccess}
          onError={onError}
          currentDomain={tenant.domain.primary}
          organizationId={organizationId}
        />
      )}
    </>
  );
};
